import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  Chip,
  TextField,
  InputAdornment,
  CircularProgress,
  Card,
  CardContent,
  Stack,
  Tooltip,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Fade,
  Zoom,
  alpha
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  CheckCircle as PostIcon,
  Undo as UnpostIcon,
  AssignmentReturn as ReturnIcon,
  TrendingDown as TrendingDownIcon,
  CheckCircle as CheckCircleIcon,
  Description as DescriptionIcon,
  Warning as WarningIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'react-toastify';
import PurchaseReturnDialog from '../components/purchaseReturns/PurchaseReturnDialog';
import PurchaseReturnPreviewDialog from '../components/purchaseReturns/PurchaseReturnPreviewDialog';
import { PurchaseReturn } from '../types/purchaseReturns.types';
import { purchaseReturnsService } from '../services/purchaseReturns.service';

const translations = {
  en: {
    title: 'Purchase Returns',
    addPurchaseReturn: 'Add Purchase Return',
    returnNumber: 'Return Number',
    returnDate: 'Return Date',
    paymentMethod: 'Payment Method',
    supplier: 'Supplier',
    warehouse: 'Warehouse',
    totalAmount: 'Total Amount',
    status: 'Status',
    actions: 'Actions',
    search: 'Search...',
    loading: 'Loading...',
    noData: 'No purchase returns found',
    confirmDelete: 'Confirm Delete',
    confirmDeleteMsg: 'Are you sure you want to delete this return?',
    confirmPost: 'Confirm Post',
    confirmPostMsg: 'Post this return? Stock will be deducted and journal entry will be posted.',
    confirmUnpost: 'Confirm Unpost',
    confirmUnpostMsg: 'Unpost this return? Stock will be restored and journal entry will be reversed.',
    draft: 'Draft',
    posted: 'Posted',
    post: 'Post',
    unpost: 'Unpost',
    totalReturns: 'Total Returns',
    activeReturns: 'Posted Returns',
    draftReturns: 'Draft Returns',
    refresh: 'Refresh',
    edit: 'Edit',
    delete: 'Delete',
    preview: 'Preview',
    rowsPerPage: 'Rows per page:',
    methodSupplier: 'Supplier',
    methodCustody: 'Custody',
    cancel: 'Cancel',
    confirm: 'Confirm',
    deleteSuccess: 'Purchase return deleted successfully',
    postSuccess: 'Purchase return posted successfully',
    unpostSuccess: 'Purchase return unposted successfully',
    deleteFailed: 'Failed to delete purchase return',
    postFailed: 'Failed to post purchase return',
    unpostFailed: 'Failed to unpost purchase return',
  },
  ar: {
    title: 'مرتجعات المشتريات',
    addPurchaseReturn: 'إضافة مرتجع',
    returnNumber: 'رقم المرتجع',
    returnDate: 'التاريخ',
    paymentMethod: 'طريقة الدفع',
    supplier: 'المورد/الجهة',
    warehouse: 'المخزن',
    totalAmount: 'الإجمالي',
    status: 'الحالة',
    actions: 'الإجراءات',
    search: 'البحث...',
    loading: 'جاري التحميل...',
    noData: 'لا توجد مرتجعات',
    confirmDelete: 'تأكيد الحذف',
    confirmDeleteMsg: 'هل أنت متأكد من حذف هذا المرتجع؟',
    confirmPost: 'تأكيد الترحيل',
    confirmPostMsg: 'هل تريد ترحيل المرتجع؟ سيتم خصم المخزون وترحيل القيد المحاسبي.',
    confirmUnpost: 'تأكيد إلغاء الترحيل',
    confirmUnpostMsg: 'هل تريد إلغاء ترحيل المرتجع؟ سيتم إرجاع المخزون وعكس القيد المحاسبي.',
    draft: 'مسودة',
    posted: 'مرحّل',
    post: 'ترحيل',
    unpost: 'إلغاء ترحيل',
    totalReturns: 'إجمالي المرتجعات',
    activeReturns: 'المرتجعات المرحّلة',
    draftReturns: 'المسودات',
    refresh: 'تحديث',
    edit: 'تعديل',
    delete: 'حذف',
    preview: 'معاينة',
    rowsPerPage: 'عدد الصفوف:',
    methodSupplier: 'مورد',
    methodCustody: 'عهدة',
    cancel: 'إلغاء',
    confirm: 'تأكيد',
    deleteSuccess: 'تم حذف المرتجع بنجاح',
    postSuccess: 'تم ترحيل المرتجع بنجاح',
    unpostSuccess: 'تم إلغاء ترحيل المرتجع بنجاح',
    deleteFailed: 'فشل حذف المرتجع',
    postFailed: 'فشل ترحيل المرتجع',
    unpostFailed: 'فشل إلغاء ترحيل المرتجع',
  },
};

const PurchaseReturnsPage: React.FC = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const isRTL = language === 'ar';

  const { userBranchIds, userWarehouseIds } = (() => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return { userBranchIds: [] as number[], userWarehouseIds: [] as number[] };
      const payload = JSON.parse(atob(token.split('.')[1]));
      const primaryBranch = payload.BranchId ? [Number(payload.BranchId)] : [];
      const allowedBranches = payload.AllowedBranchId
        ? (Array.isArray(payload.AllowedBranchId) ? payload.AllowedBranchId.map(Number) : [Number(payload.AllowedBranchId)])
        : [];
      const branchIds = Array.from(new Set([...primaryBranch, ...allowedBranches]));
      const keeperClaims = payload.KeeperWarehouseId;
      const warehouseIds = Array.isArray(keeperClaims) ? keeperClaims.map(Number) : keeperClaims ? [Number(keeperClaims)] : [];
      return { userBranchIds: branchIds, userWarehouseIds: warehouseIds };
    } catch {
      return { userBranchIds: [] as number[], userWarehouseIds: [] as number[] };
    }
  })();
  
  const canEditReturn = (pr: PurchaseReturn) => {
    if (userBranchIds.length === 0 && userWarehouseIds.length === 0) return true;
    if (pr.warehouseId && userWarehouseIds.includes(pr.warehouseId)) return true;
    if (pr.branchId && userBranchIds.includes(pr.branchId)) return true;
    return false;
  };
  
  const userRoles = (() => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return [] as string[];
      const payload = JSON.parse(atob(token.split('.')[1]));
      const roles = payload['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
      return roles ? (Array.isArray(roles) ? roles : [roles]) : [];
    } catch { return [] as string[]; }
  })();
  const canPost = (() => { try { const token = localStorage.getItem('token'); if (!token) return false; const payload = JSON.parse(atob(token.split('.')[1])); const perms = payload.Permission ? (Array.isArray(payload.Permission) ? payload.Permission : [payload.Permission]) : []; return perms.some((p: string) => p.endsWith('_POST')); } catch { return false; } })();

  const [purchaseReturns, setPurchaseReturns] = useState<PurchaseReturn[]>([]);
  const [filteredReturns, setFilteredReturns] = useState<PurchaseReturn[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedReturn, setSelectedReturn] = useState<PurchaseReturn | undefined>();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewReturn, setPreviewReturn] = useState<PurchaseReturn | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    type: 'delete' | 'post' | 'unpost' | null;
    id: number | null;
  }>({ open: false, type: null, id: null });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [stats, setStats] = useState({
    total: 0,
    posted: 0,
    draft: 0
  });

  useEffect(() => {
    loadPurchaseReturns();
  }, []);

  useEffect(() => {
    applyFilters();
    calculateStats();
  }, [searchTerm, purchaseReturns]);

  const loadPurchaseReturns = async () => {
    setLoading(true);
    try {
      const data = await purchaseReturnsService.getAll();
      const filteredData = data
        .filter((pr: PurchaseReturn) => canEditReturn(pr))
        .sort((a: PurchaseReturn, b: PurchaseReturn) => b.purchaseReturnId - a.purchaseReturnId);
      setPurchaseReturns(filteredData);
    } catch (error) {
      console.error('Failed to load purchase returns:', error);
      toast.error(isRTL ? 'فشل تحميل المرتجعات' : 'Failed to load returns');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    if (!searchTerm.trim()) {
      setFilteredReturns(purchaseReturns);
      return;
    }

    const term = searchTerm.toLowerCase();
    const filtered = purchaseReturns.filter((pr) =>
      pr.returnNumber.toLowerCase().includes(term) ||
      pr.warehouseName?.toLowerCase().includes(term) ||
      pr.supplierName?.toLowerCase().includes(term) ||
      pr.custodyName?.toLowerCase().includes(term) ||
      pr.paymentAccountName?.toLowerCase().includes(term)
    );
    setFilteredReturns(filtered);
    setPage(0);
  };

  const calculateStats = () => {
    const total = purchaseReturns.length;
    const posted = purchaseReturns.filter(pr => pr.status === 'Posted').length;
    const draft = total - posted;
    setStats({ total, posted, draft });
  };

  const handleAdd = () => {
    setSelectedReturn(undefined);
    setDialogOpen(true);
  };

  const handleEdit = (purchaseReturn: PurchaseReturn) => {
    setSelectedReturn(purchaseReturn);
    setDialogOpen(true);
  };

  const handlePreview = async (pr: PurchaseReturn) => {
    try {
      const fullData = await purchaseReturnsService.getById(pr.purchaseReturnId);
      setPreviewReturn(fullData);
      setPreviewOpen(true);
    } catch (error) {
      console.error('Failed to load return details:', error);
      toast.error(isRTL ? 'فشل تحميل التفاصيل' : 'Failed to load details');
    }
  };

  const openConfirmDialog = (type: 'delete' | 'post' | 'unpost', id: number) => {
    setConfirmDialog({ open: true, type, id });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({ open: false, type: null, id: null });
  };

  const handleConfirm = async () => {
    const { type, id } = confirmDialog;
    if (!id) return;

    closeConfirmDialog();

    try {
      switch (type) {
        case 'delete':
          await purchaseReturnsService.delete(id);
          toast.success(t.deleteSuccess);
          break;
        case 'post':
          await purchaseReturnsService.post(id);
          toast.success(t.postSuccess);
          break;
        case 'unpost':
          await purchaseReturnsService.unpost(id);
          toast.success(t.unpostSuccess);
          break;
      }
      loadPurchaseReturns();
    } catch (error: any) {
      console.error(`Failed to ${type}:`, error);
      const message = error.response?.data?.message || error.response?.data || 
        (type === 'delete' ? t.deleteFailed : type === 'post' ? t.postFailed : t.unpostFailed);
      toast.error(message);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedReturn(undefined);
  };

  const handleSave = () => {
    loadPurchaseReturns();
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedReturns = filteredReturns.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const getStatusColor = (status: string) => {
    return status === 'Posted' ? 'success' : 'warning';
  };

  const getStatusLabel = (status: string) => {
    return status === 'Posted' ? t.posted : t.draft;
  };

  const getPaymentMethodLabel = (method: string) => {
    switch (method) {
      case 'Supplier': return t.methodSupplier;
      case 'Custody': return t.methodCustody;
      default: return method;
    }
  };

  const getPayeeName = (pr: PurchaseReturn): string => {
    switch (pr.paymentMethod) {
      case 'Supplier':
        return isRTL ? (pr.supplierNameAr || pr.supplierName || '-') : (pr.supplierName || '-');
      case 'Custody':
        return isRTL ? (pr.custodyNameAr || pr.custodyName || '-') : (pr.custodyName || '-');
      case 'PaymentAccount':
        return isRTL ? (pr.paymentAccountNameAr || pr.paymentAccountName || '-') : (pr.paymentAccountName || '-');
      default:
        return '-';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'ar' ? 'ar-KW' : 'en-GB');
  };

  const getConfirmDialogContent = () => {
    const { type } = confirmDialog;
    switch (type) {
      case 'delete':
        return { title: t.confirmDelete, message: t.confirmDeleteMsg, color: '#ef4444', icon: <DeleteIcon /> };
      case 'post':
        return { title: t.confirmPost, message: t.confirmPostMsg, color: '#10b981', icon: <PostIcon /> };
      case 'unpost':
        return { title: t.confirmUnpost, message: t.confirmUnpostMsg, color: '#f59e0b', icon: <UnpostIcon /> };
      default:
        return { title: '', message: '', color: '#667eea', icon: null };
    }
  };

  const dialogContent = getConfirmDialogContent();

  return (
    <Box sx={{ p: 3 }} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Stats Cards */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Zoom in timeout={300}>
          <Box sx={{ flex: '1 1 calc(33.333% - 11px)', minWidth: '200px' }}>
            <Card 
              elevation={6} 
              sx={{ 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': { 
                  transform: 'translateY(-8px) scale(1.02)',
                  boxShadow: '0 12px 40px rgba(102, 126, 234, 0.4)'
                }
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="h3" fontWeight="bold">{stats.total}</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>{t.totalReturns}</Typography>
                  </Box>
                  <ReturnIcon sx={{ fontSize: 60, opacity: 0.3 }} />
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Zoom>

        <Zoom in timeout={400}>
          <Box sx={{ flex: '1 1 calc(33.333% - 11px)', minWidth: '200px' }}>
            <Card 
              elevation={6} 
              sx={{ 
                background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                color: 'white',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': { 
                  transform: 'translateY(-8px) scale(1.02)',
                  boxShadow: '0 12px 40px rgba(79, 172, 254, 0.4)'
                }
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="h3" fontWeight="bold">{stats.posted}</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>{t.activeReturns}</Typography>
                  </Box>
                  <CheckCircleIcon sx={{ fontSize: 60, opacity: 0.3 }} />
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Zoom>

        <Zoom in timeout={500}>
          <Box sx={{ flex: '1 1 calc(33.333% - 11px)', minWidth: '200px' }}>
            <Card 
              elevation={6} 
              sx={{ 
                background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                color: 'white',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': { 
                  transform: 'translateY(-8px) scale(1.02)',
                  boxShadow: '0 12px 40px rgba(250, 112, 154, 0.4)'
                }
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="h3" fontWeight="bold">{stats.draft}</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>{t.draftReturns}</Typography>
                  </Box>
                  <DescriptionIcon sx={{ fontSize: 60, opacity: 0.3 }} />
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Zoom>
      </Box>

      {/* Header */}
      <Fade in timeout={600}>
        <Paper 
          elevation={4} 
          sx={{ 
            p: 3, 
            mb: 3, 
            borderRadius: 3,
            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
            border: '1px solid',
            borderColor: alpha('#667eea', 0.1)
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box 
                sx={{ 
                  bgcolor: 'rgba(102, 126, 234, 0.1)',
                  p: 1.5,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <TrendingDownIcon sx={{ fontSize: 32, color: '#667eea' }} />
              </Box>
              <Box>
                <Typography variant="h5" fontWeight="bold" sx={{ color: '#667eea' }}>
                  {t.title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {isRTL ? 'إدارة مرتجعات المشتريات' : 'Manage Purchase Returns'}
                </Typography>
              </Box>
            </Box>
            <Stack direction="row" spacing={1.5}>
              <Tooltip title={t.refresh} arrow>
                <IconButton 
                  color="primary" 
                  onClick={loadPurchaseReturns}
                  sx={{ 
                    bgcolor: 'rgba(102, 126, 234, 0.1)',
                    '&:hover': { 
                      bgcolor: 'rgba(102, 126, 234, 0.2)',
                      transform: 'rotate(180deg)',
                      transition: 'transform 0.6s'
                    }
                  }}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAdd}
                sx={{
                  background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
                  boxShadow: '0 4px 20px rgba(102, 126, 234, .4)',
                  px: 3,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': { 
                    background: 'linear-gradient(45deg, #5568d3 30%, #653d8a 90%)',
                    boxShadow: '0 6px 25px rgba(102, 126, 234, .5)',
                    transform: 'translateY(-2px)'
                  }
                }}
              >
                {t.addPurchaseReturn}
              </Button>
            </Stack>
          </Box>

          <TextField
            fullWidth
            placeholder={t.search}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                bgcolor: 'background.paper',
                transition: 'all 0.3s',
                '&:hover': {
                  boxShadow: '0 2px 8px rgba(102, 126, 234, 0.15)'
                },
                '&.Mui-focused': {
                  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.2)'
                }
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#667eea' }} />
                </InputAdornment>
              ),
            }}
          />
        </Paper>
      </Fade>

      {/* Table */}
      <Fade in timeout={700}>
        <TableContainer 
          component={Paper} 
          elevation={4} 
          sx={{ 
            borderRadius: 3,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: alpha('#667eea', 0.1)
          }}
        >
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
              <CircularProgress size={60} thickness={4} sx={{ color: '#667eea' }} />
            </Box>
          ) : (
            <>
              <Table>
                <TableHead>
                  <TableRow sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.95rem' }}>{t.returnNumber}</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.95rem' }}>{t.returnDate}</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.95rem' }}>{t.paymentMethod}</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.95rem' }}>{t.supplier}</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.95rem' }}>{t.warehouse}</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.95rem' }} align="right">{t.totalAmount}</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.95rem' }}>{t.status}</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.95rem' }} align="center">{t.actions}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedReturns.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                        <TrendingDownIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary">{t.noData}</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedReturns.map((pr, index) => (
                      <TableRow 
                        key={pr.purchaseReturnId}
                        sx={{ 
                          '&:hover': { 
                            bgcolor: alpha('#667eea', 0.05),
                            transform: 'scale(1.001)',
                            boxShadow: '0 2px 8px rgba(102, 126, 234, 0.1)'
                          },
                          transition: 'all 0.2s',
                          animation: `fadeIn 0.3s ease-in ${index * 0.05}s both`,
                          '@keyframes fadeIn': {
                            from: { opacity: 0, transform: 'translateY(10px)' },
                            to: { opacity: 1, transform: 'translateY(0)' }
                          }
                        }}
                      >
                        <TableCell>
                          <Typography fontWeight="bold" color="#667eea" sx={{ fontSize: '0.95rem' }}>
                            {pr.returnNumber}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" component="div">{formatDate(pr.returnDate)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={getPaymentMethodLabel(pr.paymentMethod)} 
                            size="small" 
                            sx={{
                              bgcolor: alpha('#667eea', 0.1),
                              color: '#667eea',
                              fontWeight: 600,
                              borderRadius: 1.5
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>{getPayeeName(pr)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{isRTL ? pr.warehouseNameAr : pr.warehouseName}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography fontWeight="bold" sx={{ color: '#667eea' }}>
                            {pr.totalAmount?.toFixed(3) || '0.000'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={getStatusLabel(pr.status || 'Draft')}
                            color={getStatusColor(pr.status || 'Draft')}
                            size="small"
                            sx={{ 
                              fontWeight: 'bold',
                              minWidth: 80,
                              borderRadius: 1.5
                            }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={0.5} justifyContent="center">
                            {/* ✅ Preview Button */}
                            <Tooltip title={t.preview} arrow>
                              <IconButton
                                size="small"
                                onClick={() => handlePreview(pr)}
                                sx={{ 
                                  color: '#8b5cf6',
                                  '&:hover': { 
                                    bgcolor: alpha('#8b5cf6', 0.1),
                                    transform: 'scale(1.1)'
                                  },
                                  transition: 'all 0.2s'
                                }}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {canEditReturn(pr) && (
                              <Tooltip title={t.edit} arrow>
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleEdit(pr)}
                                    disabled={pr.status === 'Posted'}
                                    sx={{ 
                                      color: '#667eea',
                                      '&:hover': { 
                                        bgcolor: alpha('#667eea', 0.1),
                                        transform: 'scale(1.1)'
                                      },
                                      transition: 'all 0.2s'
                                    }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                            {canEditReturn(pr) && (
                              <Tooltip title={t.delete} arrow>
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => openConfirmDialog('delete', pr.purchaseReturnId)}
                                    disabled={pr.status === 'Posted'}
                                    sx={{ 
                                      color: '#ef4444',
                                      '&:hover': { 
                                        bgcolor: alpha('#ef4444', 0.1),
                                        transform: 'scale(1.1)'
                                      },
                                      transition: 'all 0.2s'
                                    }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                            {canPost && pr.status === 'Draft' && (
                              <Tooltip title={t.post} arrow>
                                <IconButton
                                  size="small"
                                  onClick={() => openConfirmDialog('post', pr.purchaseReturnId)}
                                  sx={{ 
                                    color: '#10b981',
                                    '&:hover': { 
                                      bgcolor: alpha('#10b981', 0.1),
                                      transform: 'scale(1.1)'
                                    },
                                    transition: 'all 0.2s'
                                  }}
                                >
                                  <PostIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {canPost && pr.status === 'Posted' && (
                              <Tooltip title={t.unpost} arrow>
                                <IconButton
                                  size="small"
                                  onClick={() => openConfirmDialog('unpost', pr.purchaseReturnId)}
                                  sx={{ 
                                    color: '#f59e0b',
                                    '&:hover': { 
                                      bgcolor: alpha('#f59e0b', 0.1),
                                      transform: 'scale(1.1)'
                                    },
                                    transition: 'all 0.2s'
                                  }}
                                >
                                  <UnpostIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[5, 10, 20, 50]}
                component="div"
                count={filteredReturns.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage={t.rowsPerPage}
                labelDisplayedRows={({ from, to, count }) =>
                  isRTL ? `${from}-${to} من ${count}` : `${from}-${to} of ${count}`
                }
                sx={{
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  bgcolor: alpha('#667eea', 0.02)
                }}
              />
            </>
          )}
        </TableContainer>
      </Fade>

      {/* Confirm Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={closeConfirmDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle 
          sx={{ 
            bgcolor: dialogContent.color,
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            py: 2.5
          }}
        >
          {dialogContent.icon}
          <Typography component="span" fontWeight="bold">
            {dialogContent.title}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ mt: 3, pb: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <WarningIcon sx={{ color: dialogContent.color, fontSize: 28, mt: 0.5 }} />
            <Typography variant="body1" sx={{ flex: 1, lineHeight: 1.6 }}>
              {dialogContent.message}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button 
            onClick={closeConfirmDialog}
            variant="outlined"
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              minWidth: 100,
              fontWeight: 600
            }}
          >
            {t.cancel}
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            sx={{ 
              bgcolor: dialogContent.color,
              borderRadius: 2,
              textTransform: 'none',
              minWidth: 100,
              fontWeight: 600,
              '&:hover': { 
                bgcolor: dialogContent.color,
                filter: 'brightness(0.9)'
              }
            }}
          >
            {t.confirm}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Purchase Return Dialog */}
      <PurchaseReturnDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        purchaseReturn={selectedReturn}
        onSave={handleSave}
      />

      {/* ✅ Purchase Return Preview Dialog */}
      <PurchaseReturnPreviewDialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        purchaseReturn={previewReturn}
        onEdit={() => {
          setPreviewOpen(false);
          if (previewReturn) {
            setSelectedReturn(previewReturn);
            setDialogOpen(true);
          }
        }}
      />
    </Box>
  );
};

export default PurchaseReturnsPage;